import React, { useMemo, useState, useEffect } from "react";
import FlightIcon from "@mui/icons-material/Flight";
import AirplanemodeInactiveIcon from "@mui/icons-material/AirplanemodeInactive";
import AirlineSeatReclineExtraIcon from "@mui/icons-material/AirlineSeatReclineExtra";
import TextureIcon from "@mui/icons-material/Texture";
import { ClickAwayListener, Popover, Grid, Button, styled } from "@mui/material";
import {
  // IPairing,
  IPairingEventGroup,
  IFlight,
  IPositioningFlight,
  INonFlyingAssignment,
  // IPairingRest,
} from "@vmosolutions/oc-interfaces";
import { FlightChip } from "../../Flights/FlightChip";
import { NonFlightChip } from "../../Pairings/NonFlightChip";
import { PosFlightChip } from "../../Pairings/PosFlightChip";
import { IGroundEvent } from "src/pages/TimelineEventEditors/BetaEventEditor";
import { EventChip } from "src/components/Flights/EventChip";

interface IPairingPositionCell {
  eventGroups: IPairingEventGroup[];
}

interface IPairingEvents {
  flights: IFlight[];
  positioningFlights: IPositioningFlight[];
  nonFlyingAssignments: INonFlyingAssignment[];
  groundEvents: IGroundEvent[];
}

const OCPairingEventGroupsCell = styled(Grid)(({ theme }) => ({
  flexWrap: "wrap",
  justifyContent: "flex-start",
  "& .flights": {
    backgroundColor: "forestgreen", //theme.palette.success.dark,
  },
  "& .posflights": {
    backgroundColor: "deeppink", //theme.palette.primary.main,
  },
  "& .nonflights": {
    backgroundColor: "purple", //theme.palette.secondary.dark,
  },
  "& .groundevents": {
    backgroundColor: "grey", //theme.palette.secondary.dark,
  },
  "& .button": {
    border: `1px solid ${theme.palette.background.default}`,
    color: theme.palette.primary.contrastText,
    display: "flex",
    alignItems: "flex-end",
    minWidth: "unset",
    borderRadius: 3,
    boxShadow: theme.shadows[2],
    margin: 2,
    padding: "1px 2px",
    fontSize: 10,
    fontWeight: 700,
    lineHeight: "10px",
    "&.active": {
      border: `1px solid ${theme.palette.primary.contrastText}`,
    },
    "& svg": {
      width: 16,
      height: 16,
    },
  },
}));

/**
 * Accepts array of pairing event groups and displays them in a condensed format for the search table.
 * @param props - Array of PairingEventGroups
 */
export const PairingEventGroupsCell = (props: IPairingPositionCell): JSX.Element => {
  const { eventGroups } = props;

  const [anchorEl, setAnchorEl] = useState<Element>(null);
  const [display, setDisplay] = useState<"flights" | "posflights" | "nonflights" | "groundevents">(null);

  const pairingEvents = useMemo(() => {
    const events: IPairingEvents = {
      flights: [],
      positioningFlights: [],
      nonFlyingAssignments: [],
      groundEvents: [],
    };
    eventGroups?.forEach((group: IPairingEventGroup) => {
      events.flights = [...events.flights, ...group.flights];
      events.positioningFlights = [...events.positioningFlights, ...group.positioningFlights];
      events.nonFlyingAssignments = [...events.nonFlyingAssignments, ...group.nonFlyingAssignments];
      events.groundEvents = [...events.groundEvents, ...group.groundEvents];
      return group;
    });
    return events;
  }, [eventGroups]);
  const { flights, nonFlyingAssignments, positioningFlights, groundEvents } = pairingEvents;

  const displayEvents =
    (type: "flights" | "posflights" | "nonflights" | "groundevents") =>
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
      event.stopPropagation(); // Ensured that click doesn't select/deselect table rows.
      event.persist();
      setDisplay(type);
      setAnchorEl(event.currentTarget);
    };

  useEffect(() => {
    if (!anchorEl) setDisplay(null);
  }, [anchorEl]);

  const displayFlights: JSX.Element[] = useMemo(() => {
    return flights.map((data: IFlight, index: number) => (
      <FlightChip data={data} key={`flight-${index}`} setParentAnchor={setAnchorEl} />
    ));
  }, [flights]);

  const displayNonFlights: JSX.Element[] = useMemo(() => {
    return nonFlyingAssignments.map((data: INonFlyingAssignment, index: number) => (
      <NonFlightChip data={data} key={`nonflight-${index}`} />
    ));
  }, [nonFlyingAssignments]);

  const displayPosFlights: JSX.Element[] = useMemo(() => {
    return positioningFlights.map((data: IPositioningFlight, index: number) => (
      <PosFlightChip data={data} key={`posflight-${index}`} />
    ));
  }, [positioningFlights]);

  const displayGroundEvents: JSX.Element[] = useMemo(() => {
    return groundEvents.map((data: IGroundEvent, index: number) => (
      <EventChip data={data} key={`groundevent-${index}`} />
    ));
  }, [groundEvents]);

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <OCPairingEventGroupsCell container direction="row">
        {flights.length > 0 && (
          <Button className={`flights button ${display === "flights" && "active"}`} onClick={displayEvents("flights")}>
            <FlightIcon />
            <div>{`x${flights.length}`}</div>
          </Button>
        )}
        {nonFlyingAssignments.length > 0 && (
          <Button
            className={`nonflights button ${display === "nonflights" && "active"}`}
            onClick={displayEvents("nonflights")}
          >
            <AirplanemodeInactiveIcon />
            <div>{`x${nonFlyingAssignments.length}`}</div>
          </Button>
        )}
        {positioningFlights.length > 0 && (
          <Button
            className={`posflights button ${display === "posflights" && "active"}`}
            onClick={displayEvents("posflights")}
          >
            <AirlineSeatReclineExtraIcon />
            <div>{`x${positioningFlights.length}`}</div>
          </Button>
        )}
        {groundEvents.length > 0 && (
          <Button
            className={`groundevents button ${display === "groundevents" && "active"}`}
            onClick={displayEvents("groundevents")}
          >
            <TextureIcon />
            <div>{`x${groundEvents.length}`}</div>
          </Button>
        )}
        <Popover
          anchorEl={anchorEl}
          anchorReference="anchorEl"
          anchorOrigin={{ vertical: "center", horizontal: "left" }}
          transformOrigin={{ vertical: "center", horizontal: "right" }}
          open={!!anchorEl}
          onClose={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.stopPropagation();
            setAnchorEl(null);
          }}
        >
          {display === "flights" && displayFlights}
          {display === "nonflights" && displayNonFlights}
          {display === "posflights" && displayPosFlights}
          {display === "groundevents" && displayGroundEvents}
        </Popover>
      </OCPairingEventGroupsCell>
    </ClickAwayListener>
  );
};
